import { Link } from "react-router-dom";
import { ArrowRight, Linkedin, Instagram } from "lucide-react";
import { Logo } from "@/components/site/Logo";
import { OFFICES, EMAILS } from "@/lib/content";

const COLS = [
  { title: "Explore", links: [["/brownsville-facility", "Facility"], ["/leadership", "Leadership"], ["/our-impact", "Impact"], ["/news-and-media", "News"]] },
  { title: "Get involved", links: [["/careers", "Careers"], ["/contact?audience=community", "Community"], ["/contact?audience=commercial", "Partners"], ["/contact?audience=media", "Media"]] },
];

const XIcon = () => <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true"><path d="M18.9 2H22l-7.3 8.4L23 22h-6.6l-5.2-6.8L5.2 22H2l7.8-9L1.5 2h6.7l4.7 6.2L18.9 2Zm-1.2 18h1.8L7.4 3.9H5.5L17.7 20Z" /></svg>;

const SOCIAL = [
  { label: "LinkedIn", href: "https://www.linkedin.com/company/element-fuels/", icon: <Linkedin className="h-5 w-5" /> },
  { label: "Instagram", href: "https://www.instagram.com/americafirstrefining/", icon: <Instagram className="h-5 w-5" /> },
  { label: "X", href: "https://x.com/AFRefining", icon: <XIcon /> },
  { label: "Truth Social", href: "https://truthsocial.com/@AmericaFirstRefining", icon: <span className="text-[10px] font-extrabold tracking-tight">TRUTH.</span> },
];

const linkCls = "text-sm text-white/90 underline-offset-4 hover:text-white hover:underline";

export const SiteFooter = () => (
  <footer data-testid="site-footer" className="bg-[#94691F] text-white">
    <div className="mx-auto grid max-w-[1400px] gap-12 px-5 py-16 md:grid-cols-12 md:px-10">
      <div className="md:col-span-4">
        <Logo white className="h-12 w-auto" />
        <p className="mt-5 max-w-xs text-sm text-white/90">American Fuels from American Shale for America's Energy Future.</p>
        <Link to="/contact" data-testid="footer-reach-out" className="group mt-6 inline-flex items-center gap-2 border border-white px-5 py-3 text-sm font-semibold uppercase tracking-wide transition-colors hover:bg-white hover:text-[#001870]">Reach out for more info <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></Link>
      </div>
      {COLS.map((c) => (
        <div key={c.title} className="md:col-span-2">
          <p className="font-display text-2xl font-bold uppercase">{c.title}</p>
          <ul className="mt-4 space-y-2.5">
            {c.links.map(([to, label]) => <li key={to}><Link to={to} data-testid={`footer-link-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className={linkCls}>{label}</Link></li>)}
          </ul>
        </div>
      ))}
      <div className="md:col-span-4">
        <p className="font-display text-2xl font-bold uppercase">Follow us</p>
        <div className="mt-4 flex gap-3">
          {SOCIAL.map((s) => (
            <a key={s.label} href={s.href} target="_blank" rel="noreferrer" aria-label={s.label} data-testid={`footer-social-${s.label.toLowerCase().replace(/\s+/g, "-")}`} className="flex h-11 w-11 items-center justify-center border border-white/80 transition-colors hover:bg-white hover:text-[#94691F]">{s.icon}</a>
          ))}
        </div>
        <div className="mt-6 grid gap-4 text-sm text-white/90 sm:grid-cols-2">
          {OFFICES.map((o) => <p key={o.city}><span className="font-semibold text-white">{o.city}</span><br />{o.lines.join(", ")}{o.phone && <><br />{o.phone}</>}</p>)}
        </div>
        <a href={`mailto:${EMAILS.info}`} className={`mt-3 inline-block ${linkCls}`}>{EMAILS.info}</a>
      </div>
    </div>
    <div className="border-t border-white/25">
      <div className="mx-auto flex max-w-[1400px] flex-col gap-3 px-5 py-5 text-[13px] text-white/90 md:flex-row md:items-center md:justify-between md:px-10">
        <span>© {new Date().getFullYear()} America First Refining. All Rights Reserved.</span>
        <span className="flex flex-wrap gap-5">
          <a href="https://americafirstrefining.com/privacy-policy/" className="hover:underline">Privacy Policy</a>
          <a href="https://americafirstrefining.com/terms-and-conditions/" className="hover:underline">Terms and Conditions</a>
          <Link to="/admin" data-testid="footer-link-admin" className="hover:underline">Admin</Link>
        </span>
      </div>
      <p className="mx-auto max-w-[1400px] px-5 pb-5 text-[12px] text-white/80 md:px-10">Mockup · Projections are targets, not guarantees.</p>
    </div>
  </footer>
);
